import { Box, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import dayjs from "dayjs";
import { isNaN, maxBy } from "lodash";
import React from "react";

import ReportAPI from "../../api/Report.api";
import { useCropRotationContext } from "../../contexts/CropRotation.context";
import { CropRotation, Lot } from "../../types";
import AddHarvestToLot from "./AddHarvestToLot";
import CropRotationGroup from "./CropRotationGroup";

const yearOfCropRotation = (
  cropRotation: Array<CropRotation> | CropRotation,
) => {
  let cropRotationYear: number | undefined = undefined;
  if (cropRotation instanceof Array) {
    let maxDate: Date | undefined;
    cropRotation.forEach((cropRotation) => {
      if (maxDate === undefined || cropRotation.estHarvestDate > maxDate) {
        maxDate = cropRotation.estHarvestDate;
      }
    });
    cropRotationYear = dayjs(maxDate).year();
  } else {
    cropRotationYear = dayjs(cropRotation.estHarvestDate).year();
  }
  return cropRotationYear;
};

const lotRestingDates = (lot: Array<CropRotation> | CropRotation) => {
  let estHarvestDate: Date | undefined = undefined;
  let projectedWetDate: Date | undefined = undefined;
  if (lot instanceof Array) {
    lot.forEach((cropRotation: CropRotation) => {
      if (
        estHarvestDate === undefined ||
        (cropRotation.estHarvestDate !== undefined &&
          cropRotation.estHarvestDate > estHarvestDate)
      ) {
        estHarvestDate = cropRotation.estHarvestDate;
      }
      if (
        projectedWetDate === undefined ||
        (cropRotation.projectedWetDate !== undefined &&
          cropRotation.projectedWetDate < projectedWetDate)
      ) {
        projectedWetDate = cropRotation.projectedWetDate;
      }
    });
  } else {
    estHarvestDate = lot.estHarvestDate;
    projectedWetDate = lot.projectedWetDate;
  }
  return {
    estHarvestDate,
    projectedWetDate,
  };
};

const createListOfRestingPeriod = (
  lots: (Array<CropRotation> | CropRotation)[],
) => {
  const restingPeriods: (number | undefined)[] = [];
  for (
    let leadingLotIndex = 1;
    leadingLotIndex < lots.length;
    leadingLotIndex++
  ) {
    const leadingLot = lots[leadingLotIndex];
    const leadingLotRestingDates = lotRestingDates(leadingLot);

    const trailingLot = lots[leadingLotIndex - 1];
    const trailingLotRestingDates = lotRestingDates(trailingLot);

    if (
      leadingLotRestingDates["estHarvestDate"] &&
      trailingLotRestingDates["projectedWetDate"]
    ) {
      const diffDays = dayjs(leadingLotRestingDates["projectedWetDate"]).diff(
        trailingLotRestingDates["estHarvestDate"],
        "d",
      );

      restingPeriods.push(isNaN(diffDays) ? undefined : diffDays);
    } else {
      restingPeriods.push(undefined);
    }
  }
  return restingPeriods;
};

export default function LotCropRotation({
  lot,
  numYearsBack,
  alternate,
  cropRotationMeta,
}: {
  alternate: boolean;
  lot: Lot;
  numYearsBack: number;
  cropRotationMeta: { [years: number]: number };
}) {
  const { cropWidth } = useCropRotationContext();

  const { data } = ReportAPI.useCropRotation({
    lotId: lot.id,
    numYearsBack,
  });

  // if (lot.id === 34) {
  //   console.log("data", data);
  // }

  const combineSubgroups = (data?: CropRotation[]) => {
    if (!data) return [];
    const sublotGroups = {};
    const lots: (Array<CropRotation> | CropRotation)[] = [];
    data.forEach((harvest) => {
      if (harvest.isSublot) {
        // @ts-expect-error
        if (!sublotGroups[harvest.lotable.sublotGroupId]) {
          // @ts-expect-error
          sublotGroups[harvest.lotable.sublotGroupId] = [];
        }
        // @ts-expect-error
        sublotGroups[harvest.lotable.sublotGroupId].push(harvest);
      } else {
        lots.push(harvest);
      }
    });
    // combine the two
    Object.values(sublotGroups).forEach((sublots) => {
      lots.push(sublots as CropRotation[]);
    });

    const orderedLots: (Array<CropRotation> | CropRotation)[] = lots.sort(
      (
        groupA: Array<CropRotation> | CropRotation,
        groupB: Array<CropRotation> | CropRotation,
      ) => {
        const estHarvestDateGroupA: Date | undefined = Array.isArray(groupA)
          ? maxBy(groupA, (group: CropRotation) => {
              return group.estHarvestDate;
            })?.estHarvestDate
          : groupA?.estHarvestDate;
        const estHarvestDateGroupB: Date | undefined = Array.isArray(groupB)
          ? maxBy(groupB, (group: CropRotation) => {
              return group.estHarvestDate;
            })?.estHarvestDate
          : groupB?.estHarvestDate;

        if (
          estHarvestDateGroupA === undefined &&
          estHarvestDateGroupB === undefined
        ) {
          return 0;
        } else if (estHarvestDateGroupA === undefined) {
          return -1;
        } else if (estHarvestDateGroupB === undefined) {
          return 1;
        } else if (estHarvestDateGroupA < estHarvestDateGroupB) {
          return -1;
        } else if (estHarvestDateGroupA > estHarvestDateGroupB) {
          return 1;
        }
        return 0;
      },
    );

    return orderedLots;
  };

  const lastestYear = dayjs().year();

  const lots = data ? combineSubgroups(data) : [];

  const restingPeriods = createListOfRestingPeriod(lots);

  const listItems = [];
  Object.values(cropRotationMeta).forEach((numPerYear) => {
    const fillMissing = numPerYear === 0 ? 1 : numPerYear;
    Array.from(Array(fillMissing)).forEach((val, i) => {
      listItems.push(
        <Box
          key={i}
          sx={{
            mx: "0.1rem",
            width: `${cropWidth}rem`,
            backgroundColor: grey[100],
            // border: "1px black solid",
          }}
        ></Box>,
      );
    });
  });

  const currentIndexPerYear: { [year: number]: number } = {};

  let rollingCounterOfMaxCropsPerYear = 0;
  Object.keys(cropRotationMeta)
    .sort((a: string, b: string) => parseInt(b) - parseInt(a))
    .forEach((numYearsBackIndexStr: string) => {
      const numYearsBackIndex = parseInt(numYearsBackIndexStr);
      const currentYear = lastestYear - numYearsBackIndex;
      currentIndexPerYear[currentYear] = rollingCounterOfMaxCropsPerYear;
      let maxNumCropsInYear = cropRotationMeta[numYearsBackIndex];
      if (maxNumCropsInYear === 0) {
        maxNumCropsInYear = 1;
      }
      rollingCounterOfMaxCropsPerYear += maxNumCropsInYear;
    });

  for (
    let trailingLotIndex = 0;
    trailingLotIndex < lots.length;
    trailingLotIndex++
  ) {
    const trailingCropRotation = lots[trailingLotIndex];

    const currentYear = yearOfCropRotation(trailingCropRotation);
    const listItemIndex = currentIndexPerYear[currentYear];
    currentIndexPerYear[currentYear] += 1;

    listItems[listItemIndex] = (
      <Box
        sx={{
          mx: "0.1rem",
          display: "flex",
          flexDirection: "rows",
          // border: "solid black 1px",
        }}
      >
        <CropRotationGroup cropRotations={trailingCropRotation} lot={lot} />
        <Box
          sx={{
            width: cropWidth && `${cropWidth * 0.25}rem`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography align="center">
            {restingPeriods[trailingLotIndex]}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        gap: "0.5rem",
        flex: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        {listItems.map((li, index) => (
          <React.Fragment key={index}>{li}</React.Fragment>
        ))}
      </Box>

      <AddHarvestToLot lot={lot} />
    </Box>
  );
}
